import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Typography,
} from "@mui/material"
// eslint-disable-next-line import/no-extraneous-dependencies, import/no-unresolved
import "flag-icons/css/flag-icons.min.css"
import React, { useEffect, useState } from "react"
import { useForm } from "react-hook-form"

import useEditOrganisation from "../../../services/middleware/hooks/myOrganisation/useEditOrg"
import { TGetOrgRes } from "../../../services/middleware/types/organisation/organisationTypes"
import { ValidatedPhoneNumber } from "../../inputs/validated/validatedPhoneNumber"
import AddressForm, {
    ADDRESS_KW,
    POSTCODE_KW,
} from "../../submitApplication/single/recognisedInputs/addressForm"

interface ICompanyDetailsPopupProps {
    isOpen: boolean
    isEditOrganisation: boolean
    onSubmit: () => void
    data: TGetOrgRes | undefined
    setIsOpen: (isOpen: boolean) => void
    isAdmin: boolean
}

const CompanyDetailsPopup = ({
    isOpen,
    isEditOrganisation = false,
    onSubmit,
    data,
    setIsOpen,
    isAdmin,
}: ICompanyDetailsPopupProps): React.ReactElement | null => {
    const { control, setValue, handleSubmit, getValues } = useForm()
    const [organisationName, setOrganisationName] = useState("")
    const [organisationAddressLine2, setOrganisationAddressLine2] = useState("")
    const [organisationContactNumber, setOrganisationContactNumber] =
        useState("")
    const [installerIndividualPhoneNumber, setInstallerIndividualPhoneNumber] =
        useState("")
    const [error, setError] = useState("")
    const [isDisabled, setIsDisabled] = useState(true)
    const { postResponse, isSending, error: editError } = useEditOrganisation()
    const [showPhonePopup, setShowPhonePopup] = useState(
        !installerIndividualPhoneNumber
    )
    const [phoneError, setPhoneError] = useState("")
    const [orgPhoneError, setOrgPhoneError] = useState("")

    useEffect(() => {
        if (data?.organisation && isAdmin) {
            setOrganisationName(data.organisation.organisationName || "")
            setValue(ADDRESS_KW, data.organisation.organisationAddress || "")
            setValue(POSTCODE_KW, data.organisation.organisationPostcode || "")
            setOrganisationAddressLine2(
                data.organisation.organisationAddressLine2 || ""
            )
            setOrganisationContactNumber(
                data.organisation.organisationContactNumber || ""
            )
            setInstallerIndividualPhoneNumber(
                data.organisation.installerIndividualPhoneNumber || ""
            )
            setIsDisabled(
                !(data.organisation.organisationName || "")
                    .toLowerCase()
                    .endsWith("-please_enter_company_name")
            )
        }
        if (data?.organisation && !isAdmin) {
            setInstallerIndividualPhoneNumber(
                data.organisation.installerIndividualPhoneNumber || ""
            )
        }
    }, [data, setValue, isAdmin])

    const handleIndividualPhoneValidation = (
        isValid: boolean,
        errorMessage: string
    ): void => {
        setPhoneError(errorMessage)
    }

    const handleOrgPhoneValidation = (
        isValid: boolean,
        errorMessage: string
    ): void => {
        setOrgPhoneError(errorMessage)
    }

    const submitResponse = async (): Promise<void> => {
        const organisationAddress = getValues(ADDRESS_KW)
        const organisationPostcode = getValues(POSTCODE_KW)

        if (phoneError || orgPhoneError) {
            return // Stop submission if phone validation fails
        }

        if (
            !organisationName ||
            !organisationAddress ||
            !organisationPostcode ||
            !organisationContactNumber
        ) {
            setError("All fields are required.")
            return
        }
        setError("")
        try {
            await postResponse({
                org_name: organisationName,
                org_address: organisationAddress,
                org_address_line_2: organisationAddressLine2,
                org_postcode: organisationPostcode,
                org_phone_number: organisationContactNumber.replace(/\s/g, ""),
                installer_individual_phone_number:
                    installerIndividualPhoneNumber.replace(/\s/g, ""),
            })

            onSubmit()
        } catch (err) {
            console.error("Failed to update organisation details:", editError)
        }
    }

    const submitNonAdminResponse = async (): Promise<void> => {
        if (phoneError) {
            return // Stop submission if phone validation fails
        }
        setError("")
        try {
            // post to edit-org endpoint is failing due to missing required fields for non-admin users.
            // this form is only shown when "installerIndividualPhoneNumber" is missing, hence,
            // using existing org data for org details
            // cleaner solution would be to have a separate endpoint for updating installerIndividualPhoneNumber for non-admin users,
            // but this is a quicker fix to unblock the issue.
            // This can be refactored later to separate endpoints if needed.
            const orgName = data?.organisation.organisationName || ""
            const orgAddress = data?.organisation.organisationAddress || ""
            const orgPostcode = data?.organisation.organisationPostcode || ""
            const orgAddressLine2 =
                data?.organisation.organisationAddressLine2 || ""

            const orgPhoneNumber =
                data?.organisation.organisationContactNumber || ""

            await postResponse({
                org_name: orgName,
                org_address: orgAddress,
                org_address_line_2: orgAddressLine2,
                org_postcode: orgPostcode,
                org_phone_number: orgPhoneNumber.replace(/\s/g, ""),
                installer_individual_phone_number:
                    installerIndividualPhoneNumber.replace(/\s/g, ""),
            })
            onSubmit()
        } catch (err) {
            console.error(
                "Failed to update individual phone number:",
                editError
            )
            setError("Failed to save phone number. Please try again.")
        }
    }

    if (isAdmin) {
        return (
            <Dialog open={isOpen}>
                <DialogTitle>Enter Your Organisation Details</DialogTitle>
                <DialogContent>
                    <Typography
                        variant="body2"
                        color="textSecondary"
                        gutterBottom
                    >
                        Please provide your details and these will be added to
                        your application.
                    </Typography>

                    <TextField
                        fullWidth
                        margin="normal"
                        label="Organisation Name"
                        value={organisationName}
                        onChange={(e) => setOrganisationName(e.target.value)}
                        required
                        disabled={isDisabled}
                    />
                    <AddressForm
                        control={control}
                        disabled={false}
                        setValue={setValue}
                        isDefaults={!!getValues(POSTCODE_KW)}
                        label="Search for Organisation's Post Code"
                        worldwide
                    />
                    <TextField
                        fullWidth
                        margin="normal"
                        label="Organisation Address (Line 2)"
                        value={organisationAddressLine2}
                        onChange={(e) =>
                            setOrganisationAddressLine2(e.target.value)
                        }
                    />
                    <ValidatedPhoneNumber
                        value={organisationContactNumber}
                        label="Organisation Phone Number"
                        onChange={setOrganisationContactNumber}
                        onValidationChange={handleOrgPhoneValidation}
                        required
                        disabled={false}
                        helperText="Please enter the organisation's contact number"
                    />
                    <ValidatedPhoneNumber
                        value={installerIndividualPhoneNumber}
                        label="Individual Phone Number"
                        onChange={setInstallerIndividualPhoneNumber}
                        onValidationChange={handleIndividualPhoneValidation}
                        required={false}
                        disabled={false}
                        helperText="Please update optional Individual Phone Number."
                    />
                    {error && (
                        <Typography variant="body2" color="error" gutterBottom>
                            {error}
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    {isEditOrganisation ? (
                        <Button
                            onClick={() => setIsOpen(false)}
                            color="secondary"
                            variant="contained"
                        >
                            Close
                        </Button>
                    ) : null}
                    <Button
                        onClick={handleSubmit(submitResponse)}
                        color="primary"
                        variant="contained"
                        disabled={isSending || !!phoneError || !!orgPhoneError}
                    >
                        Submit
                    </Button>
                </DialogActions>
            </Dialog>
        )
    }

    if (!isAdmin && showPhonePopup) {
        return (
            <Dialog open={isOpen}>
                <DialogTitle>Enter Your Individual Details</DialogTitle>
                <DialogContent>
                    <ValidatedPhoneNumber
                        value={installerIndividualPhoneNumber}
                        label="Individual Phone Number"
                        onChange={setInstallerIndividualPhoneNumber}
                        onValidationChange={handleIndividualPhoneValidation}
                        required={false}
                        disabled={false}
                        helperText="Please update optional Individual Phone Number."
                    />
                    {error && (
                        <Typography variant="body2" color="error" gutterBottom>
                            {error}
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            setShowPhonePopup(false)
                            setIsOpen(false)
                        }}
                        color="secondary"
                        variant="contained"
                    >
                        Close
                    </Button>
                    <Button
                        onClick={() => {
                            submitNonAdminResponse()
                            setShowPhonePopup(false)
                        }}
                        color="primary"
                        variant="contained"
                        disabled={isSending || !!phoneError}
                    >
                        Submit
                    </Button>
                </DialogActions>
            </Dialog>
        )
    }

    // Return null if dialog shouldn't be shown
    return null
}

export default CompanyDetailsPopup
